import React, { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import '../CopyTrading/copytrading.css';
import Swal from 'sweetalert2';

/** Fallback when page is opened via URL (no location.state). */
const DEFAULT_TRADER = {
    name: 'Dearmhe',
    badge: 'DE',
    pnl: '+30.2%',
    usdt: '+158.51 USDT',
    cpnl: '-0.63 USDT',
    ls: '2.7K USDT',
    days: '42',
};

const CopyTradingDetail = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const traderNameFromQuery = searchParams.get('trader');
    const tabFromQuery = searchParams.get('tab');

    const selectedTrader =
        location.state?.trader ||
        (traderNameFromQuery
            ? { ...DEFAULT_TRADER, name: traderNameFromQuery }
            : DEFAULT_TRADER);
    const activeTab = location.state?.activeTab || tabFromQuery || 'Futures';

    // Form inputs and toggle states
    const [amount, setAmount] = useState('');
    const [amountPercentage, setAmountPercentage] = useState(0);
    const [copyMode, setCopyMode] = useState('Copy positions with a better price');
    const [isCopyModeDropdownOpen, setIsCopyModeDropdownOpen] = useState(false);
    const [totalStopLoss, setTotalStopLoss] = useState('');
    const [showMoreSettings, setShowMoreSettings] = useState(true);
    const [copyingMethod, setCopyingMethod] = useState('Proportional to Total');
    const [isCopyingMethodDropdownOpen, setIsCopyingMethodDropdownOpen] = useState(false);
    const [leverageMode, setLeverageMode] = useState("Copy the trader's leverage");
    const [isLeverageModeDropdownOpen, setIsLeverageModeDropdownOpen] = useState(false);
    const [takeProfit, setTakeProfit] = useState('');
    const [takeProfitPercentage, setTakeProfitPercentage] = useState(0);
    const [stopLoss, setStopLoss] = useState('');
    const [stopLossPercentage, setStopLossPercentage] = useState(0);
    const [limitPerTx, setLimitPerTx] = useState('');
    const [amrChecked, setAmrChecked] = useState(false);
    const [rulesChecked, setRulesChecked] = useState(true);

    // Helper functions for stable random-like stats on the detail page
    const getCopiers = (trader) => {
        if (!trader) return "615";
        if (trader.copiers) return trader.copiers.split('/')[0];
        const seed = trader.name.length;
        return Math.floor((seed * 23.4) % 300) + 120;
    };

    const getDays = (trader) => {
        if (!trader) return "447";
        if (trader.days) return trader.days;
        const seed = trader.name.length;
        return Math.floor((seed * 17.8) % 180) + 30;
    };

    const getAUM = (trader) => {
        if (!trader) return "115,110.53";
        if (trader.ls) {
            let val = trader.ls.replace(' USDT', '');
            if (val.includes('K')) {
                val = (parseFloat(val.replace('K', '')) * 1000).toLocaleString('en-US', { minimumFractionDigits: 2 });
            }
            return val;
        }
        const seed = trader.name.length;
        const val = (seed * 8543.21) % 90000 + 15000;
        return val.toLocaleString('en-US', { minimumFractionDigits: 2 });
    };

    return (
        <div className="copy_settings_wrapper copy_detailpage">
            <div className="container">
                <div className="breadcrumbs">
                    <span onClick={() => {
                        navigate('/copy-trading', { state: { activeTab } });
                        window.scrollTo(0, 0);
                    }}>{activeTab === 'Futures' ? 'Futures Copy Trading' : 'CFD Copy Trading'}</span>
                    <span>&gt;</span>
                    <span>Copy Settings</span>
                </div>

                <h1 className="copy_settings_title">Copy Settings</h1>

                <div className="copy_settings_layout">
                    {/* Left Form Column */}
                    <div className="copy_settings_left">

                        {/* Amount Field */}
                        <div className="form_group">
                            <label className="form_label">Amount</label>
                            <div className="input_box_wrapper">
                                <input
                                    type="number"
                                    placeholder="20-50000"
                                    value={amount}
                                    onChange={(e) => {
                                        setAmount(e.target.value);
                                        const val = parseFloat(e.target.value);
                                        if (val >= 20) {
                                            setAmountPercentage(Math.min(100, Math.floor(((val - 20) / 49980) * 100)));
                                        } else {
                                            setAmountPercentage(0);
                                        }
                                    }}
                                />
                                <span className="input_suffix">
                                    USDT | <span className="input_suffix_max" onClick={() => {
                                        setAmount('50000');
                                        setAmountPercentage(100);
                                    }}>MAX</span>
                                </span>
                            </div>
                            <div className="slider_container">
                                <div className="slider_track_wrapper">
                                    <div className="slider_dots">
                                        {[0, 25, 50, 75, 100].map((dot) => (
                                            <div
                                                key={dot}
                                                className={`slider_dot ${amountPercentage >= dot ? 'active' : ''}`}
                                            />
                                        ))}
                                    </div>
                                </div>
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={amountPercentage}
                                    onChange={(e) => {
                                        const pct = parseInt(e.target.value);
                                        setAmountPercentage(pct);
                                        setAmount(Math.floor(20 + (pct / 100) * 49980));
                                    }}
                                    className="slider_bar_input"
                                />
                            </div>
                            <div className="available_info">
                                <span>Available</span>
                                <span className="transfer_link">
                                    <strong>0.00 USDT</strong> <i class="ri-arrow-left-right-line"></i>
                                </span>
                            </div>
                        </div>

                        {/* Existing Position Copy Mode */}
                        <div className="form_group">
                            <label className="form_label">Existing Position Copy Mode</label>
                            <div className="custom_select_box" onClick={() => setIsCopyModeDropdownOpen(!isCopyModeDropdownOpen)}>
                                <span className="custom_select_value">{copyMode}</span>
                                <span className="custom_select_arrow">
                                    <i className={isCopyModeDropdownOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                                </span>
                                {isCopyModeDropdownOpen && (
                                    <div className="custom_select_options">
                                        {["Copy positions with a better price", "Copy positions instantly"].map((opt) => (
                                            <div
                                                key={opt}
                                                className="custom_option"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCopyMode(opt);
                                                    setIsCopyModeDropdownOpen(false);
                                                }}
                                            >
                                                {opt}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Total Stop Loss */}
                        <div className="form_group">
                            <label className="form_label">Total Stop Loss</label>
                            <div className="input_box_wrapper">
                                <input
                                    type="number"
                                    placeholder="1-95"
                                    value={totalStopLoss}
                                    onChange={(e) => setTotalStopLoss(e.target.value)}
                                />
                                <span className="input_suffix">%</span>
                            </div>
                            <ul className="form_note_list">
                                <li>1. Your copied trade will automatically close at the market price, and copy trading will stop once your remaining balance falls below the specified USDT amount (including unrealized PnL).</li>
                                <li>2. Due to stop-loss settings, orders may experience slippage when closed at market price. AGCX is not liable for any potential losses incurred during copy trading.</li>
                            </ul>
                        </div>

                        {/* More Settings Header */}
                        <div className="more_settings_header" onClick={() => setShowMoreSettings(!showMoreSettings)}>
                            <h3>More Settings</h3>
                            <span className="custom_select_arrow">
                                <i className={showMoreSettings ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                            </span>
                        </div>

                        {/* Collapsible Content */}
                        {showMoreSettings && (
                            <div className="more_settings_content">

                                {/* Copying Method */}
                                <div className="form_group">
                                    <label className="form_label">Copying Method</label>
                                    <div className="custom_select_box" onClick={() => setIsCopyingMethodDropdownOpen(!isCopyingMethodDropdownOpen)}>
                                        <span className="custom_select_value">{copyingMethod}</span>
                                        <span className="custom_select_arrow">
                                            <i className={isCopyingMethodDropdownOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                                        </span>
                                        {isCopyingMethodDropdownOpen && (
                                            <div className="custom_select_options">
                                                {["Proportional to Total", "Fixed Ratio"].map((opt) => (
                                                    <div
                                                        key={opt}
                                                        className="custom_option"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setCopyingMethod(opt);
                                                            setIsCopyingMethodDropdownOpen(false);
                                                        }}
                                                    >
                                                        {opt}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Leverage */}
                                <div className="form_group">
                                    <label className="form_label">Leverage</label>
                                    <div className="custom_select_box" onClick={() => setIsLeverageModeDropdownOpen(!isLeverageModeDropdownOpen)}>
                                        <span className="custom_select_value">{leverageMode}</span>
                                        <span className="custom_select_arrow">
                                            <i className={isLeverageModeDropdownOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                                        </span>
                                        {isLeverageModeDropdownOpen && (
                                            <div className="custom_select_options">
                                                {["Copy the trader's leverage", "Custom leverage"].map((opt) => (
                                                    <div
                                                        key={opt}
                                                        className="custom_option"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setLeverageMode(opt);
                                                            setIsLeverageModeDropdownOpen(false);
                                                        }}
                                                    >
                                                        {opt}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Take Profit Per Position */}
                                <div className="form_group">
                                    <label className="form_label">Take Profit Per Position</label>
                                    <div className="input_box_wrapper">
                                        <input
                                            type="number"
                                            placeholder="0%"
                                            value={takeProfit}
                                            onChange={(e) => {
                                                setTakeProfit(e.target.value);
                                                setTakeProfitPercentage(Math.min(100, parseInt(e.target.value) || 0));
                                            }}
                                        />
                                        <span className="input_suffix">%</span>
                                    </div>
                                    <div className="slider_container">
                                        <div className="slider_track_wrapper">
                                            <div className="slider_dots">
                                                {[0, 25, 50, 75, 100].map((dot) => (
                                                    <div
                                                        key={dot}
                                                        className={`slider_dot ${takeProfitPercentage >= dot ? 'active' : ''}`}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                        <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            value={takeProfitPercentage}
                                            onChange={(e) => {
                                                setTakeProfitPercentage(parseInt(e.target.value));
                                                setTakeProfit(e.target.value);
                                            }}
                                            className="slider_bar_input"
                                        />
                                    </div>
                                </div>

                                {/* Stop Loss Per Position */}
                                <div className="form_group">
                                    <label className="form_label">Stop Loss Per Position</label>
                                    <div className="input_box_wrapper">
                                        <input
                                            type="number"
                                            placeholder="1-99%"
                                            value={stopLoss}
                                            onChange={(e) => {
                                                setStopLoss(e.target.value);
                                                setStopLossPercentage(Math.min(100, parseInt(e.target.value) || 0));
                                            }}
                                        />
                                        <span className="input_suffix">%</span>
                                    </div>
                                    <div className="slider_container">
                                        <div className="slider_track_wrapper">
                                            <div className="slider_dots">
                                                {[0, 25, 50, 75, 100].map((dot) => (
                                                    <div
                                                        key={dot}
                                                        className={`slider_dot ${stopLossPercentage >= dot ? 'active' : ''}`}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                        <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            value={stopLossPercentage}
                                            onChange={(e) => {
                                                setStopLossPercentage(parseInt(e.target.value));
                                                setStopLoss(e.target.value);
                                            }}
                                            className="slider_bar_input"
                                        />
                                    </div>
                                </div>

                                {/* Limit Per Transaction */}
                                <div className="form_group">
                                    <label className="form_label">Limit Per Transaction</label>
                                    <div className="input_box_wrapper">
                                        <input
                                            type="number"
                                            placeholder="20-50000"
                                            value={limitPerTx}
                                            onChange={(e) => setLimitPerTx(e.target.value)}
                                        />
                                        <span className="input_suffix">USDT</span>
                                    </div>
                                </div>

                                {/* Markets */}
                                <div className="markets_row">
                                    <span className="form_label">Markets</span>
                                    <div className="markets_right">
                                        <div className="markets_coins">
                                            <span className="coin_icon coin_btc">
                                                <svg width="20" height="20" viewBox="0 0 32 32">
                                                    <circle cx="16" cy="16" r="16" fill="#F7931A" />
                                                    <path fill="#FFF" d="M22.28 14.5c.29-1.95-1.19-2.99-3.21-3.69l.66-2.63-1.6-.4-.64 2.57c-.42-.1-.85-.2-1.27-.3l.65-2.6-1.6-.4-.66 2.64c-.35-.08-.69-.16-1.02-.24l.01-.04-2.2-.55-.43 1.71s1.18.27 1.16.29c.65.16.76.59.74.93l-.74 2.98c.04.01.1.03.16.05-.05-.01-.11-.03-.16-.05l-1.04 4.16c-.08.2-.28.5-.73.39.02.02-1.16-.29-1.16-.29l-.8 1.84 2.08.52c.39.1.77.2(1.15.29l-.66 2.68 1.6.4.66-2.65c.44.12.86.23 1.28.33l-.65 2.62 1.6.4.67-2.68c2.73.52 4.79.31 5.65-2.16.7-1.98-.03-3.13-1.46-3.88.6-.27 1.05-.77 1.25-1.69zm-2.23 5.48c-.5 1.99-3.84.91-4.92.65l.88-3.52c1.08.27 4.57.8 4.04 2.87zm.49-5.52c-.45 1.81-3.23.89-4.13.67l.8-3.22c.9.22 3.8.64 3.33 2.55z" />
                                                </svg>
                                            </span>
                                            <span className="coin_icon coin_eth">
                                                <svg width="20" height="20" viewBox="0 0 32 32">
                                                    <circle cx="16" cy="16" r="16" fill="#627EEA" />
                                                    <path fill="#FFF" fillOpacity=".8" d="M16.48 4v9.64l6.02 2.76L16.48 4z" />
                                                    <path fill="#FFF" d="M16.48 4L10.5 16.4l6.02-2.76V4z" />
                                                    <path fill="#FFF" fillOpacity=".8" d="M16.48 20.25V28l6.04-8.52-6.04.77z" />
                                                    <path fill="#FFF" d="M16.48 28.02v-7.77l-6.02-.75 6.02 8.52z" />
                                                    <path fill="#FFF" fillOpacity=".5" d="M16.48 19.12l5.98-2.72-5.98-2.76v5.48z" />
                                                    <path fill="#FFF" fillOpacity=".8" d="M16.48 13.64v5.48l-5.98-2.76 5.98-2.72z" />
                                                </svg>
                                            </span>
                                            <span className="coin_icon coin_green_btc">
                                                <svg width="20" height="20" viewBox="0 0 32 32">
                                                    <circle cx="16" cy="16" r="16" fill="#00C087" />
                                                    <path fill="#FFF" d="M22.28 14.5c.29-1.95-1.19-2.99-3.21-3.69l.66-2.63-1.6-.4-.64 2.57c-.42-.1-.85-.2-1.27-.3l.65-2.6-1.6-.4-.66 2.64c-.35-.08-.69-.16-1.02-.24l.01-.04-2.2-.55-.43 1.71s1.18.27 1.16.29c.65.16.76.59.74.93l-.74 2.98c.04.01.1.03.16.05-.05-.01-.11-.03-.16-.05l-1.04 4.16c-.08.2-.28.5-.73.39.02.02-1.16-.29-1.16-.29l-.8 1.84 2.08.52c.39.1.77.2(1.15.29l-.66 2.68 1.6.4.66-2.65c.44.12.86.23 1.28.33l-.65 2.62 1.6.4.67-2.68c2.73.52 4.79.31 5.65-2.16.7-1.98-.03-3.13-1.46-3.88.6-.27 1.05-.77 1.25-1.69zm-2.23 5.48c-.5 1.99-3.84.91-4.92.65l.88-3.52c1.08.27 4.57.8 4.04 2.87zm.49-5.52c-.45 1.81-3.23.89-4.13.67l.8-3.22c.9.22 3.8.64 3.33 2.55z" />
                                                </svg>
                                            </span>
                                        </div>
                                        <span className="markets_text">3/7 <i className="ri-arrow-right-s-line"></i></span>
                                    </div>
                                </div>

                                {/* Auto Margin Replenishment */}
                                <div className="form_group">
                                    <div className="toggle_row">
                                        <label className="form_label">Auto Margin Replenishment (AMR)</label>
                                        <label className="toggle_switch">
                                            <input
                                                type="checkbox"
                                                checked={amrChecked}
                                                onChange={(e) => setAmrChecked(e.target.checked)}
                                            />
                                            <span className="slider_toggle"></span>
                                        </label>
                                    </div>
                                    <div className="note_box">
                                        Due to differences in copy capital, leverage, and entry timing, your copy trading ROI may differ from the lead trader's ROI.
                                    </div>
                                </div>

                            </div>
                        )}

                        {/* Terms Checkbox */}
                        <div className="checkbox_rule">
                            <input
                                type="checkbox"
                                id="terms"
                                checked={rulesChecked}
                                onChange={(e) => setRulesChecked(e.target.checked)}
                            />
                            <label htmlFor="terms">
                                You have fully understood AGCe Copy Trading Rules And Potential Risks Involved and confirm to copy
                            </label>
                        </div>

                        {/* Submit Button */}
                        <button
                            className="btn_submit_copy"
                            onClick={() => {
                                if (!amount) {
                                    Swal.fire({
                                        icon: 'error',
                                        title: 'Error',
                                        text: 'Please enter a valid amount!',
                                        confirmButtonColor: '#1e2329'
                                    });
                                    return;
                                }
                                if (!rulesChecked) {
                                    Swal.fire({
                                        icon: 'error',
                                        title: 'Error',
                                        text: 'You must agree to the Copy Trading Rules!',
                                        confirmButtonColor: '#1e2329'
                                    });
                                    return;
                                }
                                Swal.fire({
                                    icon: 'success',
                                    title: 'Success!',
                                    text: `Successfully copied ${selectedTrader.name}!`,
                                    confirmButtonColor: '#1e2329'
                                }).then(() => {
                                    navigate('/copy-trading', { state: { activeTab } });
                                    window.scrollTo(0, 0);
                                });
                            }}
                        >
                            Copy
                        </button>

                    </div>

                    {/* Right Sidebar Column */}
                    <div className="copy_settings_right">
                        <div className="sidebar_trader_card">
                            <div className="stc_header">
                                {selectedTrader.avatar ? (
                                    <img src={selectedTrader.avatar} alt="avatar" className="stc_avatar" />
                                ) : (
                                    <div className="stc_avatar_placeholder">
                                        {selectedTrader.badge || selectedTrader.name.substring(0, 2).toUpperCase()}
                                    </div>
                                )}
                                <div className="stc_name_section">
                                    <div className="stc_name">
                                        <span className="stc_name_text" title={selectedTrader.name}>{selectedTrader.name}</span>
                                        <span className="star_badge"><i className="ri-star-fill"></i></span>
                                    </div>
                                    <div className="stc_tags">
                                        <span className="stc_tag_pill">Short-term</span>
                                        <span className="stc_tag_pill">High Frequency</span>
                                        <span className="stc_tag_pill">Conservative</span>
                                    </div>
                                </div>
                            </div>

                            <p className="stc_bio">No introduction yet</p>

                            <div className="stc_stats_grid">
                                <div className="stc_stat_item">
                                    <span className="stc_stat_label">Copiers</span>
                                    <span className="stc_stat_value">
                                        {getCopiers(selectedTrader)}
                                    </span>
                                </div>
                                <div className="stc_stat_item">
                                    <span className="stc_stat_label">Joined Day(s)</span>
                                    <span className="stc_stat_value">
                                        {getDays(selectedTrader)}
                                    </span>
                                </div>
                                <div className="stc_stat_item">
                                    <span className="stc_stat_label">Ratio</span>
                                    <span className="stc_stat_value">10%</span>
                                </div>
                                <div className="stc_stat_item">
                                    <span className="stc_stat_label">AUM</span>
                                    <span className="stc_stat_value">
                                        {getAUM(selectedTrader)}
                                    </span>
                                </div>
                            </div>

                            <div className="stc_links">
                                <div className="stc_link_item">
                                    <i className="ri-list-check"></i>
                                    <span>Step-by-step Tutorial</span>
                                </div>
                                <div className="stc_link_item">
                                    <i className="ri-question-line"></i>
                                    <span>Help Documentation</span>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default CopyTradingDetail;
